import React, { useState } from "react";
import { PrimaryButton, SecondaryButton } from "./Buttons.jsx";
import ModalBasic from "./ModalBasic.jsx";
import { FaShield } from "react-icons/fa6";

function MFACodeModal({ isOpen, onClose, onSubmit, loading = false, error = null }) {
  const [mfaCode, setMfaCode] = useState("");
  const [isBackupCode, setIsBackupCode] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const expectedLength = isBackupCode ? 11 : 6; // XXXXX-XXXXX = 11 chars, 6-digit code = 6 chars
    if (mfaCode && mfaCode.length === expectedLength) {
      onSubmit(mfaCode);
    } else {
    }
  };

  const handleCodeChange = (e) => {
    let value = e.target.value.toUpperCase();
    
    // Check if this looks like a backup code (contains letters)
    const hasLetters = /[A-Z]/.test(value);
    setIsBackupCode(hasLetters);
    
    if (hasLetters) {
      // Backup code format: XXXXX-XXXXX
      // Remove all non-alphanumeric characters except dash
      value = value.replace(/[^A-Z0-9-]/g, '');
      
      // Auto-add dash after 5 characters
      if (value.length > 5 && !value.includes('-')) {
        value = value.slice(0, 5) + '-' + value.slice(5);
      }
      
      // Limit to 11 characters (5 + dash + 5)
      if (value.length > 11) {
        value = value.slice(0, 11);
      }
    } else {
      // TOTP code format: 6 digits only
      value = value.replace(/\D/g, '');
      if (value.length > 6) {
        value = value.slice(0, 6);
      }
    }
    
    setMfaCode(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const expectedLength = isBackupCode ? 11 : 6;
      if (mfaCode && mfaCode.length === expectedLength) {
        onSubmit(mfaCode);
      }
    }
  };

  const handleClose = () => {
    setMfaCode("");
    setIsBackupCode(false);
    onClose();
  };

  return (
    <ModalBasic
      id="mfa-code-modal"
      modalOpen={isOpen}
      setModalOpen={handleClose}
      width={"xl"}
    >
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/50 mx-auto mb-4">
            <FaShield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 text-center mb-2">
            Two-Factor Authentication
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-center">
            {isBackupCode 
              ? "Enter your backup code (format: XXXXX-XXXXX)" 
              : "Enter the 6-digit code from your authenticator app or backup code"
            }
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400 text-center">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {isBackupCode ? "Backup Code" : "Authentication Code"}
            </label>
            <input
              type="text"
              maxLength={isBackupCode ? 11 : 6}
              placeholder={isBackupCode ? "ABCDE-FGHIJ" : "123456"}
              value={mfaCode}
              onChange={handleCodeChange}
              onKeyDown={handleKeyDown}
              className="form-input w-full text-center text-2xl tracking-widest"
              autoComplete="off"
              autoFocus
              disabled={loading}
            />
            {isBackupCode && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 text-center">
                Format: 5 letters, dash, 5 letters
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <SecondaryButton onClick={handleClose} disabled={loading}>
              Cancel
            </SecondaryButton>
            <PrimaryButton 
              type="submit"
              disabled={loading || (isBackupCode ? mfaCode.length !== 11 : mfaCode.length !== 6)}
            >
              {loading ? "Verifying..." : "Verify"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </ModalBasic>
  );
}

export default MFACodeModal;
